import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'

import { RichTextEditor } from './RichTextEditor'
import type { ArticleFormState, FormErrors } from '../types/article-form.types'
import type { TroubleshootingRow } from '../types/article.types'

type ArticleTechnicalSectionProps = {
  errors: FormErrors
  form: ArticleFormState
  setField: <K extends keyof ArticleFormState>(field: K) => (value: ArticleFormState[K]) => void
  setRichTextField: (field: 'problem' | 'solution') => (value: string) => void
  handleInlineImagePaste: (field: 'problem' | 'solution', file: File) => Promise<string>
  addTroubleshootingRow: () => void
  updateTroubleshootingRow: (index: number, field: keyof TroubleshootingRow, value: string) => void
  removeTroubleshootingRow: (index: number) => void
}

export function ArticleTechnicalSection({
  errors,
  form,
  setField,
  setRichTextField,
  handleInlineImagePaste,
  addTroubleshootingRow,
  updateTroubleshootingRow,
  removeTroubleshootingRow,
}: ArticleTechnicalSectionProps) {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" color="primary" mb={0.5}>
        3. Technical Content
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Main technical knowledge captured by the engineer.
      </Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        <TextField
          label="Summary"
          fullWidth
          multiline
          rows={3}
          value={form.summary}
          onChange={event => setField('summary')(event.target.value)}
          error={Boolean(errors.summary)}
          helperText={errors.summary}
          placeholder="Brief explanation of the issue, method, or lesson learned."
          required={true}
        />
        <RichTextEditor
          label="Problem / Background"
          value={form.problem}
          onChange={setRichTextField('problem')}
          onPasteImage={(file) => handleInlineImagePaste('problem', file)}
          error={Boolean(errors.problem)}
          helperText={errors.problem}
          placeholder="Describe the issue, context, and technical background. You can paste pictures."
          required={true}
        />
        <TextField
          label="Symptoms"
          fullWidth
          multiline
          rows={3}
          value={form.symptoms}
          onChange={event => setField('symptoms')(event.target.value)}
          placeholder="List observed machine behavior, alarms, failures, or operator symptoms."
        />
        <TextField
          label="Root Cause"
          fullWidth
          multiline
          rows={3}
          value={form.root_cause}
          onChange={event => setField('root_cause')(event.target.value)}
          placeholder="Document the confirmed or suspected root cause."
        />
        <RichTextEditor
          label="Solution / Procedure"
          value={form.solution}
          onChange={setRichTextField('solution')}
          onPasteImage={(file) => handleInlineImagePaste('solution', file)}
          error={Boolean(errors.solution)}
          helperText={errors.solution}
          placeholder="Describe the recommended solution or the step-by-step procedure. You can paste pictures."
          required={true}
        />

        <Box>
          <Typography variant="subtitle2" mb={1}>
            Troubleshooting Table
          </Typography>
          <Box sx={{ border: '1px solid #d8e0ea', borderRadius: 2, overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ color: '#6b7280', fontSize: 13, width: '28%' }}>Issue</TableCell>
                  <TableCell sx={{ color: '#6b7280', fontSize: 13, width: '32%' }}>Possible Cause</TableCell>
                  <TableCell sx={{ color: '#6b7280', fontSize: 13 }}>Recommended Action</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {form.troubleshooting_notes.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ p: 0.5 }}>
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        value={row.issue}
                        onChange={event => updateTroubleshootingRow(index, 'issue', event.target.value)}
                        placeholder="Example: Flash fails"
                        InputProps={{ disableUnderline: true, sx: { px: 1 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        value={row.possible_cause}
                        onChange={event => updateTroubleshootingRow(index, 'possible_cause', event.target.value)}
                        placeholder="Example: Bad cable"
                        InputProps={{ disableUnderline: true, sx: { px: 1 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        value={row.recommended_action}
                        onChange={event => updateTroubleshootingRow(index, 'recommended_action', event.target.value)}
                        placeholder="Example: Verify DB9 wiring"
                        InputProps={{ disableUnderline: true, sx: { px: 1 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      {form.troubleshooting_notes.length > 1 && (
                        <IconButton size="small" onClick={() => removeTroubleshootingRow(index)}>
                          <DeleteIcon fontSize="small" sx={{ color: '#b42318' }} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Button size="small" startIcon={<AddIcon />} onClick={addTroubleshootingRow} sx={{ mt: 1 }}>
            Add Row
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}
